import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { get } from "../../../services/lewooApi";
import { StatCard, PageHeader, LoadingScreen } from "../../../components/admin/EssUi";
import { ClipboardList, Search } from "lucide-react";

const ROWS_PER_PAGE = 10;

const mapChannelType = (type) => {
  const map = {
    documentation: "Documentation",
    video: "Video",
    article: "Article",
    quiz: "Quiz",
    infographic: "Infographic",
  };
  return map[type] || "Training";
};

const mapPublishedStatus = (published) => {
  return published ? "In Progress" : "Scheduled";
};

const AdminPanelTrainingPage = () => {
  const intl = useIntl();
  const { permissions } = useSelector((state) => state.auth) || {};
  const canManage = !!permissions?.canManageTraining;
  const [data, setData] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [activeTab, setActiveTab] = useState("sessions");
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);

  useEffect(() => {
    (async () => {
      try {
        const r = await get("/api/slide-channels");
        const channels = r?.data || [];
        const mapped = channels.map((e, i) => ({
          id: e.id || i,
          title: e.name || "—",
          category: mapChannelType(e.channel_type),
          facilitator: e.user_id || "—",
          enrolled: e.members_count || 0,
          scheduled: "—",
          duration: "—",
          costHead: "$0",
          status: mapPublishedStatus(e.website_published),
          totalViews: e.total_views || 0,
          totalSlides: e.total_slides || 0,
        }));
        setData(mapped);
      } catch { setData([]); }
      setLoaded(true);
    })();
  }, []);

  if (!loaded) return <LoadingScreen />;

  const cats = [...new Set(data.map(r => r.category))];
  const statuses = [...new Set(data.map(r => r.status))];

  const getTabFilter = () => {
    if (activeTab === "inprogress") return { key: "status", value: "In Progress" };
    if (activeTab === "completed") return { key: "status", value: "Completed" };
    if (activeTab === "scheduled") return { key: "status", value: "Scheduled" };
    return null;
  };
  const tabFilter = getTabFilter();

  const filtered = data.filter(r => {
    if (tabFilter && r.status !== tabFilter.value) return false;
    if (statusFilter !== "all" && r.status !== statusFilter) return false;
    if (typeFilter !== "all" && r.category !== typeFilter) return false;
    if (search.trim() && !r.title.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / ROWS_PER_PAGE));
  const paginatedData = filtered.slice((page - 1) * ROWS_PER_PAGE, page * ROWS_PER_PAGE);

  const tabs = [
    { key: "sessions", label: "All Sessions", count: data.length },
    { key: "inprogress", label: "In Progress", count: data.filter(r => r.status === "In Progress").length },
    { key: "scheduled", label: "Scheduled", count: data.filter(r => r.status === "Scheduled").length },
    { key: "completed", label: "Completed", count: data.filter(r => r.status === "Completed").length },
  ];

  const getStatusColor = (status) => {
    const colors = { "Scheduled": "#4f46e5", "In Progress": "#d97706", "Completed": "#16a34a", "Requested": "#7c3aed", "Draft": "#94a3b8" };
    return colors[status] || "#64748b";
  };

  const getStatusBg = (status) => {
    const colors = { "Scheduled": "#eef2ff", "In Progress": "#fef3c7", "Completed": "#dcfce7", "Requested": "#f3e8ff", "Draft": "#f1f5f9" };
    return colors[status] || "#f1f5f9";
  };

  const getCategoryColor = (category) => {
    const colors = { "Technical": "#4f46e5", "Soft Skills": "#16a34a", "Compliance": "#d97706", "Safety": "#db2777", "Documentation": "#7c3aed", "Training": "#64748b" };
    return colors[category] || "#64748b";
  };

  const getCategoryBg = (category) => {
    const colors = { "Technical": "#eef2ff", "Soft Skills": "#dcfce7", "Compliance": "#fef3c7", "Safety": "#fce7f3", "Documentation": "#f3e8ff", "Training": "#f1f5f9" };
    return colors[category] || "#f1f5f9";
  };

  return (
    <div className="max-w-7xl mx-auto animate-fade-in" style={{ padding: '0 16px' }}>
      <PageHeader
        title="Training"
        subtitle="Manage training requests, sessions and completions"
        actions={
          <div style={{ display: 'flex', gap: '12px' }}>
            {canManage && (
            <button className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all shadow-sm" style={{ background: "#059669", color: "white", border: 'none', cursor: 'pointer' }}>
              ＋ New Training
            </button>
            )}
            <button className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all" style={{ background: "#f1f5f9", color: "#334155", border: 'none', cursor: 'pointer' }}>
              <ClipboardList size={16} />
              Requests
            </button>
          </div>
        }
      />

      <div className="ess-sg ess-sg4" style={{ marginBottom: '24px' }}>
        <StatCard label="Active Sessions" value={data.filter(r => r.status === "In Progress").length} icon="🎓" color="indigo" trend="Currently running" />
        <StatCard label="Scheduled" value={data.filter(r => r.status === "Scheduled").length} icon="📅" color="amber" trend="Upcoming" />
        <StatCard label="Completed" value={data.filter(r => r.status === "Completed").length} icon="✅" color="green" trend="All time" />
        <StatCard label="Total Enrolled" value={data.reduce((s, r) => s + (r.enrolled || 0), 0)} icon="👥" color="blue" trend="Across all sessions" />
      </div>

      <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', borderBottom: '1px solid #e2e8f0', paddingBottom: '4px', overflowX: 'auto' }}>
        {tabs.map((tab) => (
          <button key={tab.key} onClick={() => { setActiveTab(tab.key); setPage(1); }}
            style={{
              padding: '8px 20px', border: 'none', background: activeTab === tab.key ? '#059669' : 'transparent',
              color: activeTab === tab.key ? 'white' : '#475569', borderRadius: '8px', fontSize: '13px',
              fontWeight: activeTab === tab.key ? '600' : '500', cursor: 'pointer', transition: 'all 0.2s',
              display: 'flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap'
            }}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span style={{ background: activeTab === tab.key ? 'rgba(255,255,255,0.2)' : '#f1f5f9', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600' }}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '24px', background: 'white', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
        <div style={{ flex: 1, minWidth: '200px', position: 'relative' }}>
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#94a3b8' }} />
          <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search training..."
            style={{ width: '100%', padding: '10px 12px 10px 36px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', background: '#f8fafc' }}
            onFocus={(e) => e.target.style.borderColor = '#4f46e5'}
            onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
          />
        </div>
        <select value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
          style={{ padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', background: '#f8fafc', outline: 'none', cursor: 'pointer', minWidth: '160px' }}
        >
          <option value="all">All Types</option>
          {cats.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          style={{ padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', background: '#f8fafc', outline: 'none', cursor: 'pointer', minWidth: '160px' }}
        >
          <option value="all">All Status</option>
          {statuses.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                <th style={{ textAlign: 'left', padding: '14px 16px', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Training</th>
                <th style={{ textAlign: 'left', padding: '14px 16px', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Category</th>
                <th style={{ textAlign: 'left', padding: '14px 16px', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Facilitator</th>
                <th style={{ textAlign: 'center', padding: '14px 16px', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Enrolled</th>
                <th style={{ textAlign: 'center', padding: '14px 16px', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Sessions</th>
                <th style={{ textAlign: 'center', padding: '14px 16px', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Views</th>
                <th style={{ textAlign: 'left', padding: '14px 16px', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</th>
                <th style={{ textAlign: 'right', padding: '14px 16px', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}></th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.length === 0 ? (
                <tr><td colSpan={8} style={{ padding: '40px 16px', textAlign: 'center', color: '#94a3b8', fontSize: '14px' }}>No training sessions found</td></tr>
              ) : paginatedData.map((row, index) => (
                <tr key={row.id} style={{ borderBottom: index < paginatedData.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                  <td style={{ padding: '14px 16px', fontSize: '14px', fontWeight: '600', color: '#1e293b', maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.title}</td>
                  <td style={{ padding: '14px 16px' }}>
                    <span style={{ background: getCategoryBg(row.category), color: getCategoryColor(row.category), padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '500', whiteSpace: 'nowrap' }}>{row.category}</span>
                  </td>
                  <td style={{ padding: '14px 16px', fontSize: '13px', color: '#475569' }}>{row.facilitator}</td>
                  <td style={{ padding: '14px 16px', fontSize: '13px', fontWeight: '600', color: '#1e293b', textAlign: 'center' }}>{row.enrolled}</td>
                  <td style={{ padding: '14px 16px', fontSize: '13px', color: '#64748b', textAlign: 'center' }}>{row.totalSlides || "—"}</td>
                  <td style={{ padding: '14px 16px', fontSize: '13px', color: '#64748b', textAlign: 'center' }}>{row.totalViews || "—"}</td>
                  <td style={{ padding: '14px 16px' }}>
                    <span style={{ background: getStatusBg(row.status), color: getStatusColor(row.status), padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' }}>{row.status}</span>
                  </td>
                  <td style={{ padding: '14px 16px', textAlign: 'right' }}>
                    <button style={{ border: 'none', background: 'transparent', color: '#4f46e5', fontSize: '13px', fontWeight: '500', cursor: 'pointer', padding: '4px 8px', borderRadius: '4px' }}
                      onMouseEnter={(e) => e.target.style.background = '#eef2ff'}
                      onMouseLeave={(e) => e.target.style.background = 'transparent'}
                    >View</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', marginTop: '16px' }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
            style={{ padding: '8px 16px', border: '1px solid #e2e8f0', borderRadius: '8px', background: page === 1 ? '#f8fafc' : 'white', color: page === 1 ? '#94a3b8' : '#059669', cursor: page === 1 ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: '500' }}
          >← Previous</button>
          <span style={{ fontSize: '13px', color: '#64748b' }}>Page {page} of {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
            style={{ padding: '8px 16px', border: '1px solid #e2e8f0', borderRadius: '8px', background: page === totalPages ? '#f8fafc' : 'white', color: page === totalPages ? '#94a3b8' : '#059669', cursor: page === totalPages ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: '500' }}
          >Next →</button>
        </div>
      )}
    </div>
  );
};

export default AdminPanelTrainingPage;
